import React, { useEffect, useState, ReactNode } from "react"
import { Controller } from "react-hook-form"
import { toast } from "react-toastify"
import { handleUpdateBandData } from "../BandEditPage"
import { useBandEditForm } from "./BandEditFormContext"

// "Location (required)" -> "Location", "Your Bandcamp URL" -> "Bandcamp URL" -
// field labels are written for the label text above the input, not for
// reuse in a sentence, so clean them up before appending "Saved".
const toSavedToastLabel = (label: string): string =>
  label.replace(/\s*\(required\)\s*/i, "").replace(/^Your\s+/i, "").trim()

type ValidationState =
  | "valid"
  | "invalid"
  | "warning"
  | "validating"
  | "waiting"
  | "idle"

// How long to wait after the last keystroke before actually saving - avoids
// firing a save (and a server round-trip) on every single character typed.
const AUTO_SAVE_DEBOUNCE_MS = 600

// Light "does this look like a URL" check for fields that want one (see the
// `validate` prop below) - tries the value as typed, then falls back to
// assuming https:// if the protocol was left off. Intentionally lenient:
// this only drives a soft "warning" state, it never blocks the save.
//
// The browser's URL parser is more forgiving than you'd expect - it happily
// percent-encodes spaces and accepts "this is not a url" as a syntactically
// valid (if silly) hostname. Rejecting raw whitespace and requiring a
// dotted, letter-only-ish TLD on the hostname filters those out while still
// accepting bare domains like "example.com" or "instagram.com/someband".
export const isLikelyUrl = (value: string): boolean => {
  const trimmed = value.trim()
  if (!trimmed) return true
  if (/\s/.test(trimmed)) return false

  const tryParse = (candidate: string): URL | null => {
    try {
      return new URL(candidate)
    } catch {
      return null
    }
  }

  const parsed = tryParse(trimmed) || tryParse(`https://${trimmed}`)
  if (!parsed) return false

  return /\.[a-z]{2,}$/i.test(parsed.hostname)
}

interface SelectOption {
  value: string | null
  label: string
  description?: string
}

interface FieldData {
  field: string // Can be a nested path like "additional_info.hours"
  name: string
  label: string
  placeholder?: string
  required?: boolean
  maxLength?: number
  notes?: string | ReactNode
  serviceClass?: string
}

interface ApiResponse {
  success: boolean
  errors?: {
    error: string
  }
}

interface EditBandFormFieldProps {
  fieldData: FieldData
  value?: string | null // Optional now, will come from form
  setValue?: (value: string | null) => void // Optional now, will use form.setValue
  bandId?: number | null // Optional now, will come from context
  onSubmit?: (data: Record<string, any>) => Promise<ApiResponse>
  type?: "text" | "textarea" | "select" | "number"
  options?: SelectOption[]
  isArray?: boolean
  // Optional light/soft format check (e.g. isLikelyUrl) - a failing value
  // still saves normally, it just shows a "warning" state instead of
  // "valid" so the field doesn't look silently wrong.
  validate?: (value: string) => boolean
}

export const EditBandFormField: React.FC<EditBandFormFieldProps> = ({
  fieldData,
  value: propValue,
  setValue: propSetValue,
  bandId: propBandId,
  onSubmit = null,
  type = "text",
  options = [],
  isArray = false,
  validate,
}) => {
  const { form, bandId: contextBandId } = useBandEditForm()
  const bandId = propBandId || contextBandId
  const fieldName = fieldData.field

  // Watch the field value from the form
  const watchedValue = form.watch(fieldName)
  const value = propValue !== undefined ? propValue : watchedValue || ""

  const [validationState, setValidationState] = useState<ValidationState>(
    value ? (validate && !validate(value) ? "warning" : "valid") : "idle",
  )
  const [validationError, setValidationError] = useState<string | null>(null)
  const [initialValue] = useState<string | null>(value)

  const handleResponse = (response: ApiResponse, currentValue: string) => {
    if (response?.success) {
      if (validate && currentValue && !validate(currentValue)) {
        setValidationState("warning")
        setValidationError("Doesn't look like a valid URL - saved anyway")
        return
      }
      setValidationState("valid")
      setValidationError(null)
      // toastId (per field) dedupes rapid-fire saves while typing into a
      // single toast instead of stacking one per keystroke - same pattern
      // as trackListingToast in PlaylistListItem/Expanded.tsx.
      toast.success(`${toSavedToastLabel(fieldData.label)} Saved`, {
        toastId: `band-field-saved-${fieldName}`,
        position: "bottom-right",
        autoClose: 1500,
        hideProgressBar: true,
        closeOnClick: true,
      })
    } else {
      setValidationState("invalid")
      setValidationError(response.errors?.error || "An error occurred")
    }
  }

  // Auto-save when value changes, debounced so a burst of keystrokes (or
  // rapid select changes) collapses into a single save once things settle,
  // rather than firing a request per change. The field stays in "waiting"
  // (set on focus) for the debounce window, which already reads correctly
  // as "not saved yet".
  useEffect(() => {
    const currentValue = watchedValue
    if (
      currentValue === initialValue ||
      currentValue === undefined ||
      currentValue === null
    ) {
      return
    }

    const timeoutId = setTimeout(() => {
      // Build update data based on field path (handle nested paths like "additional_info.hours")
      let updateData: Record<string, any> = {}
      const fieldValue =
        type === "select" && isArray ? [currentValue] : currentValue

      // Handle nested field paths (e.g., "additional_info.hours" -> { additional_info: { hours: value } })
      if (fieldName.includes(".")) {
        const parts = fieldName.split(".")
        const lastPart = parts[parts.length - 1]
        let nested = updateData
        for (let i = 0; i < parts.length - 1; i++) {
          nested[parts[i]] = nested[parts[i]] || {}
          nested = nested[parts[i]]
        }
        nested[lastPart] = fieldValue
      } else {
        updateData[fieldName] = fieldValue
      }

      setValidationState("validating")

      const savePromise = onSubmit
        ? onSubmit(updateData)
        : handleUpdateBandData(bandId, updateData)

      savePromise
        .then((response) => handleResponse(response, currentValue))
        .catch((error) => {
          setValidationState("invalid")
          setValidationError("An error occurred")
        })
    }, AUTO_SAVE_DEBOUNCE_MS)

    return () => clearTimeout(timeoutId)
  }, [
    watchedValue,
    bandId,
    fieldData.field,
    initialValue,
    onSubmit,
    validate,
    type,
    isArray,
    fieldName,
  ])

  // Get description for select fields
  const getDescription = (): string | undefined => {
    const selectedOption = options?.find((option) => option.value === value)
    return selectedOption?.description
  }

  const getValidationClasses = (): string => {
    return validationState === "valid"
      ? "border-green-500"
      : validationState === "invalid"
        ? "border-red-500"
        : validationState === "warning"
          ? "border-orange-400"
          : validationState === "validating"
            ? "border-yellow-500"
            : ""
  }

  const getValidationEmoji = (): string => {
    return validationState === "valid"
      ? "🤘"
      : validationState === "invalid"
        ? "👎"
        : validationState === "warning"
          ? "⚠️"
          : validationState === "validating"
            ? "🤞"
            : validationState === "waiting"
              ? "🤔"
              : ""
  }

  const renderInput = () => {
    const baseClasses = `col-span-11 shadow appearance-none border rounded-sm w-full text-base py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${getValidationClasses()}`

    return (
      <Controller
        name={fieldName}
        control={form.control}
        render={({ field }) => {
          switch (type) {
            case "textarea":
              return (
                <textarea
                  {...field}
                  className={`${baseClasses} resize-none min-h-[100px]`}
                  name={fieldData.name}
                  placeholder={fieldData.placeholder}
                  required={fieldData.required}
                  maxLength={fieldData.maxLength}
                  value={field.value || ""}
                  onFocus={() => setValidationState("waiting")}
                  onChange={(e) => {
                    field.onChange(e)
                    // Auto-resize the textarea
                    e.target.style.height = "auto"
                    e.target.style.height = e.target.scrollHeight + "px"
                  }}
                  rows={4}
                  style={{ minHeight: "100px" }}
                />
              )

            case "select":
              return (
                <select
                  {...field}
                  className={baseClasses}
                  value={field.value || ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  required={fieldData.required}
                >
                  {options.map((option) => (
                    <option
                      key={option.value || "null"}
                      value={option.value || ""}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              )

            case "number":
              return (
                <input
                  {...field}
                  className={baseClasses}
                  type="number"
                  min={0}
                  name={fieldData.name}
                  placeholder={fieldData.placeholder}
                  required={fieldData.required}
                  value={field.value || ""}
                  onFocus={() => setValidationState("waiting")}
                />
              )

            default: // text
              return (
                <input
                  {...field}
                  className={baseClasses}
                  type="text"
                  name={fieldData.name}
                  placeholder={fieldData.placeholder}
                  required={fieldData.required}
                  maxLength={fieldData.maxLength}
                  value={field.value || ""}
                  onFocus={() => setValidationState("waiting")}
                />
              )
          }
        }}
      />
    )
  }

  return (
    <div className="grid grid-cols-12 mt-10">
      <label
        className="block col-span-12 text-gray-900 text-xs uppercase font-bold"
        htmlFor={fieldData.name}
      >
        {fieldData.label}
      </label>
      {renderInput()}
      <div className="col-span-1 pl-2 text-2xl">{getValidationEmoji()}</div>
      <div
        className={`col-span-12 text-sm ${
          validationState === "warning" ? "text-orange-600" : "text-red-700"
        }`}
      >
        {validationError}
      </div>
      {fieldData.maxLength != null && (
        <div className="col-span-12 pt-1 text-gray-500 text-sm">
          {(value || "").length} / {fieldData.maxLength}
        </div>
      )}
      {type === "select" && value && (
        <div className="col-span-12 pt-1 text-gray-600 text-sm">
          {getDescription()}
        </div>
      )}
      {fieldData.notes && (
        <div className="col-span-12 pt-1 text-gray-600 text-sm">
          {typeof fieldData.notes === "string" ? (
            <span dangerouslySetInnerHTML={{ __html: fieldData.notes }} />
          ) : (
            fieldData.notes
          )}
        </div>
      )}
    </div>
  )
}
